/**
 * Subsystem registry. Owned by core; changes need lead sign-off.
 *
 * The order here IS the frame order from ARCHITECTURE.md §2. Do not reorder.
 *
 * Static imports so Vite bundles everything. Each subsystem file is scaffolded with the
 * correct class shape from day one — an owner fills theirs in without anyone else's build
 * breaking. If a constructor throws, core swaps in a NullSubsystem and the game still boots.
 */

import { MaterialLibrary } from '../materials/library.js';
import { LookSystem } from '../render/look.js';
import { WorldSystem } from '../world/world.js';
import { VehicleSystem } from '../vehicle/vehicle.js';
import { TrafficSystem } from '../traffic/traffic.js';
import { GameplaySystem } from '../gameplay/gameplay.js';
import { FxSystem } from '../fx/fx.js';
import { AudioSystem } from '../audio/audio.js';
import { UiSystem } from '../ui/ui.js';

export const SUBSYSTEMS = [
  { id: 'materials', Ctor: MaterialLibrary, isLibrary: true },
  { id: 'look', Ctor: LookSystem },
  { id: 'world', Ctor: WorldSystem },
  { id: 'vehicle', Ctor: VehicleSystem },
  { id: 'traffic', Ctor: TrafficSystem },
  { id: 'gameplay', Ctor: GameplaySystem },
  { id: 'fx', Ctor: FxSystem },
  { id: 'audio', Ctor: AudioSystem },
  { id: 'ui', Ctor: UiSystem },
];

/**
 * Fixed-step order (ARCHITECTURE §2 step 2).
 *
 * `fx` is here because §7.3 requires particles to advance only in fixed steps so shots are
 * bit-identical — but the original list omitted it, so `fx.fixedUpdate()` was never called
 * and the fx owner had to drive its own accumulator off ctx.time.sim. That was a bug in
 * this file, not in fx. It runs after `world` so it sees the fully-stepped world.
 */
export const FIXED_ORDER = ['vehicle', 'traffic', 'gameplay', 'world', 'fx'];

/**
 * Presentation order (ARCHITECTURE §2 steps 4-10).
 *
 * `gameplay` was omitted from this list originally — the same class of bug as `fx` missing
 * from FIXED_ORDER. Its update() was never called, so pothole geometry and cone instance
 * matrices were never written and the whole field was invisible. The owner worked around it
 * by syncing visuals from the tail of fixedUpdate(), which §2 forbids (presentation must not
 * run inside the sim step). It sits right after `vehicle` so its visuals are current before
 * `fx` and `look` consume them.
 */
export const UPDATE_ORDER = ['vehicle', 'gameplay', 'fx', 'world', 'look', 'audio', 'ui'];

export class NullSubsystem {
  constructor(id) { this.id = id; this.isNull = true; }
  async init() {}
  fixedUpdate() {}
  update() {}
  onResize() {}
  dispose() {}
}

export function loadSubsystem(entry, ctx) {
  try {
    const inst = new entry.Ctor(ctx);
    inst.id = entry.id;
    if (inst.stub) console.info(`[registry] ${entry.id} is still a stub`);
    return inst;
  } catch (err) {
    console.error(`[registry] ${entry.id} constructor threw — using null stub`, err);
    return new NullSubsystem(entry.id);
  }
}
